import '@aileron/icon';
import { css, LitElement, html } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';

var __defProp$4 = Object.defineProperty;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$4(obj, key, {
  enumerable: true,
  configurable: true,
  writable: true,
  value
}) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {})) if (__hasOwnProp$2.call(b, prop)) __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2) for (var prop of __getOwnPropSymbols$2(b)) {
    if (__propIsEnum$2.call(b, prop)) __defNormalProp$2(a, prop, b[prop]);
  }
  return a;
};
function emit(el, name, options) {
  const event = new CustomEvent(name, __spreadValues$2({
    bubbles: true,
    cancelable: false,
    composed: true,
    detail: {}
  }, options));
  el.dispatchEvent(event);
  return event;
}

var __defProp$3 = Object.defineProperty;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$3(obj, key, {
  enumerable: true,
  configurable: true,
  writable: true,
  value
}) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {})) if (__hasOwnProp$1.call(b, prop)) __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1) for (var prop of __getOwnPropSymbols$1(b)) {
    if (__propIsEnum$1.call(b, prop)) __defNormalProp$1(a, prop, b[prop]);
  }
  return a;
};
function watch(propName, options) {
  const resolvedOptions = __spreadValues$1({
    waitUntilFirstUpdate: false
  }, options);
  return (proto, decoratedFnName) => {
    const {
      update
    } = proto;
    if (propName in proto) {
      const propNameKey = propName;
      proto.update = function (changedProps) {
        if (changedProps.has(propNameKey)) {
          const oldValue = changedProps.get(propNameKey);
          const newValue = this[propNameKey];
          if (oldValue !== newValue) {
            if (!resolvedOptions.waitUntilFirstUpdate || this.hasUpdated) {
              this[decoratedFnName](oldValue, newValue);
            }
          }
        }
        update.call(this, changedProps);
      };
    }
  };
}

const lightThemeStyles = css`
:root,
:host,
:host::after,
:host::before {
  --size-w1: 1rem;
  --size-w2: 2rem;
  --size-w4: 4rem;
  --size-w8: 8rem;
  --size-w12: 12rem;
  --size-w16: 16rem;
  --size-w24: 24rem;
  --size-w32: 32rem;
  --size-w40: 40rem;
  --size-w48: 48rem;
  --size-w64: 64rem;
  --size-w80: 80rem;
  --size-w96: 96rem;
  --size-w180: 160rem;
  --breakpoint-xs: 390;
  --breakpoint-sm: 800;
  --breakpoint-md: 1280;
  --breakpoint-lg: 1440;
  --spacing-2: 0.125rem;
  --spacing-4: 0.25rem;
  --spacing-8: 0.5rem;
  --spacing-12: 0.75rem;
  --spacing-16: 1rem;
  --spacing-24: 1.5rem;
  --spacing-32: 2rem;
  --spacing-36: 2.25rem;
  --spacing-40: 2.5rem;
  --spacing-48: 3rem;
  --spacing-64: 4rem;
  --spacing-80: 5rem;
  --spacing-96: 6rem;
  --spacing-180: 11.25rem;
  --radius-sm: 0.25rem;
  --radius-lg: 0.5rem;
  --color-light-mode-background-container-default: #ffffff;
  --color-light-mode-background-container-secondary: #f9fafb;
  --color-light-mode-background-container-tertiary: #f3f5f7;
  --color-light-mode-background-selected-container-default: #ffffff;
  --color-light-mode-background-selected-container-secondary: #f9fafb;
  --color-light-mode-background-selected-container-tertiary: #f3f5f7;
  --color-light-mode-background-container-interactive: #0860c4;
  --color-light-mode-background-container-status-information: #0860c4;
  --color-light-mode-background-container-status-success: #316833;
  --color-light-mode-background-container-status-warning: #ae3f13;
  --color-light-mode-background-container-status-error: #cd1d32;
  --color-light-mode-background-container-status-information-on-container-default: #f5faff;
  --color-light-mode-background-container-status-information-on-container-secondary: #ecf4fe;
  --color-light-mode-background-container-status-information-on-container-tertiary: #e2effe;
  --color-light-mode-background-container-status-success-on-container-default: #f1f8f1;
  --color-light-mode-background-container-status-success-on-container-secondary: #eaf5eb;
  --color-light-mode-background-container-status-success-on-container-tertiary: #e3f2e4;
  --color-light-mode-background-container-status-warning-on-container-default: #fef8f6;
  --color-light-mode-background-container-status-warning-on-container-secondary: #fdf1ed;
  --color-light-mode-background-container-status-warning-on-container-tertiary: #fceae3;
  --color-light-mode-background-container-status-error-on-container-default: #fef6f7;
  --color-light-mode-background-container-status-error-on-container-secondaryv: #fcedef;
  --color-light-mode-background-container-status-error-on-container-tertiary: #fbe4e7;
  --color-light-mode-text-text-default: #101419;
  --color-light-mode-text-text-secondary: #50657c;
  --color-light-mode-text-text-disabled: #b4c1cf;
  --color-light-mode-text-text-interactive: #0860c4;
  --color-light-mode-text-text-on-container-interactive: #ffffff;
  --color-light-mode-text-text-status-information: #0860c4;
  --color-light-mode-text-text-status-success: #316833;
  --color-light-mode-text-text-status-warning: #ae3f13;
  --color-light-mode-text-text-status-error: #cd1d32;
  --color-light-mode-icon-icon-default: #101419;
  --color-light-mode-icon-icon-secondary: #50657c;
  --color-light-mode-icon-icon-disabled: #b4c1cf;
  --color-light-mode-icon-icon-interactive: #0860c4;
  --color-light-mode-icon-icon-on-container-interactive: #ffffff;
  --color-light-mode-icon-icon-status-information: #0a78f5;
  --color-light-mode-icon-icon-status-success: #418b44;
  --color-light-mode-icon-icon-status-warning: #dc5018;
  --color-light-mode-icon-icon-status-error: #e33b4e;
  --color-light-mode-border-border-default: #637e9c;
  --color-light-mode-border-border-secondary: #b4c1cf;
  --color-light-mode-border-border-tertiary: #e6eaef;
  --color-light-mode-border-border-interactive: #0860c4;
  --color-light-mode-border-border-selected: #0a78f5;
  --color-light-mode-border-border-status-information: #0a78f5;
  --color-light-mode-border-border-status-success: #418b44;
  --color-light-mode-border-border-status-warning: #dc5018;
  --color-light-mode-border-border-status-error: #e33b4e;
  --color-light-mode-interactive-hover-hover-on-container-default: #f9fafb;
  --color-light-mode-interactive-hover-hover-on-container-secondary: #f3f5f7;
  --color-light-mode-interactive-hover-hover-on-container-tertiary: #eceff3;
  --color-light-mode-interactive-hover-hover-on-interactive: #0a78f5;
  --color-light-mode-interactive-hover-hover-on-selected-container-default: #f5faff;
  --color-light-mode-interactive-hover-hover-on-selected-container-secondary: #ecf4fe;
  --color-light-mode-interactive-hover-hover-on-selected-container-tertiary: #e2effe;
  --color-light-mode-interactive-pressed-pressed-on-container-default: #f3f5f7;
  --color-light-mode-interactive-pressed-pressed-on-container-secondary: #eceff3;
  --color-light-mode-interactive-pressed-pressed-on-container-tertiary: #e6eaef;
  --color-light-mode-interactive-pressed-pressed-on-interactive: #3b93f7;
  --color-light-mode-interactive-pressed-pressed-on-selected-container-default: #ecf4fe;
  --color-light-mode-interactive-pressed-pressed-on-selected-container-secondary: #e2effe;
  --color-light-mode-interactive-pressed-pressed-on-selected-container-tertiary: #d8e9fd;
  --color-light-mode-interactive-focus-outline-focus: #0a78f5;
  --color-light-mode-interactive-disabled-disabled-on-container-interactive: #b4c1cf;
  --color-light-mode-interactive-disabled-disabled-on-border-interactive: #b4c1cf;
  --neutral-140: #ffffff;
  --neutral-130: #f9fafb;
  --neutral-120: #f3f5f7;
  --neutral-110: #eceff3;
  --neutral-100: #e6eaef;
  --neutral-090: #b4c1cf;
  --neutral-080: #8398af;
  --neutral-070: #637e9c;
  --neutral-060: #50657c;
  --neutral-050: #405164;
  --neutral-040: #303d4b;
  --neutral-030: #28323e;
  --neutral-020: #202832;
  --neutral-010: #181e25;
  --neutral-000: #101419;
  --blue-140: #ffffff;
  --blue-130: #f5faff;
  --blue-120: #ecf4fe;
  --blue-110: #e2effe;
  --blue-100: #d8e9fd;
  --blue-090: #89befa;
  --blue-080: #3b93f7;
  --blue-070: #0a78f5;
  --blue-060: #0860c4;
  --blue-050: #074d9d;
  --blue-040: #053976;
  --blue-030: #043062;
  --blue-020: #03264e;
  --blue-010: #021d3c;
  --blue-000: #021327;
  --green-140: #ffffff;
  --green-130: #f1f8f1;
  --green-120: #eaf5eb;
  --green-110: #e3f2e4;
  --green-100: #dcefdd;
  --green-080: #52ad55;
  --green-070: #418b44;
  --green-060: #316833;
  --green-040: #244c25;
  --green-030: #1d3e1e;
  --green-020: #173118;
  --green-010: #102311;
  --green-000: #0a150a;
  --orange-140: #ffffff;
  --orange-130: #fef8f6;
  --orange-120: #fdf1ed;
  --orange-110: #fceae3;
  --orange-100: #fbe4da;
  --orange-080: #ea6f3e;
  --orange-070: #dc5018;
  --orange-060: #ae3f13;
  --orange-040: #6e280c;
  --orange-030: #5c210a;
  --orange-020: #491b08;
  --orange-010: #371406;
  --orange-000: #250d04;
  --red-140: #ffffff;
  --red-130: #fef6f7;
  --red-120: #fcedef;
  --red-110: #fbe4e7;
  --red-100: #fbe0e3;
  --red-080: #e96776;
  --red-070: #e33b4e;
  --red-060: #cd1d32;
  --red-040: #74111c;
  --red-030: #620e18;
  --red-020: #500b14;
  --red-010: #3e090f;
  --red-000: #2d060b;
  --grid-390-column-gutter-size: 1rem;
  --grid-390-column-count: 4;
  --grid-390-column-margin: 1rem;
  --grid-800-column-gutter-size: 1.5rem;
  --grid-800-column-count: 8;
  --grid-800-column-margin: 2rem;
  --grid-1280-column-gutter-size: 1.5rem;
  --grid-1280-column-count: 12;
  --grid-1280-column-margin: 2rem;
  --grid-1440-column-gutter-size: 1.5rem;
  --grid-1440-column-count: 12;
  --grid-1440-column-margin: auto;
  --elevation-01: 0px 3px 1px 0px rgba(0 0 0 / 0.12), 0px 2px 2px 0px rgba(0 0 0 / 0.14), 0px 1px 5px 0px rgba(0 0 0 / 0.2);
  --elevation-02: 0px 1px 18px 0px rgba(0 0 0 / 0.12), 0px 6px 10px 0px rgba(0 0 0 / 0.14), 0px 3px 5px -1px rgba(0 0 0 / 0.2);
  --elevation-03: 0px 3px 14px 2px rgba(0 0 0 / 0.12), 0px 8px 10px 1px rgba(0 0 0 / 0.14), 0px 5px 5px -3px rgba(0 0 0 / 0.2);
  --elevation-04: 0px 5px 22px 4px rgba(0 0 0 / 0.12), 0px 12px 17px 2px rgba(0 0 0 / 0.14), 0px 7px 8px -4px rgba(0 0 0 / 0.2);
  --elevation-05: 0px 9px 46px 8px rgba(0 0 0 / 0.12), 0px 24px 38px 3px rgba(0 0 0 / 0.14), 0px 11px 15px -7px rgba(0 0 0 / 0.2);
  --typography-390-heading-title-font-size: 36;
  --typography-390-heading-title-text-decoration: none;
  --typography-390-heading-title-font-family: AmericanSans;
  --typography-390-heading-title-font-weight: 400;
  --typography-390-heading-title-font-style: normal;
  --typography-390-heading-title-font-stretch: normal;
  --typography-390-heading-title-letter-spacing: 0;
  --typography-390-heading-title-line-height: 48;
  --typography-390-heading-title-paragraph-indent: 0;
  --typography-390-heading-title-paragraph-spacing: 0;
  --typography-390-heading-title-text-case: none;
  --typography-390-heading-h1-font-size: 32;
  --typography-390-heading-h1-text-decoration: none;
  --typography-390-heading-h1-font-family: AmericanSans;
  --typography-390-heading-h1-font-weight: 400;
  --typography-390-heading-h1-font-style: normal;
  --typography-390-heading-h1-font-stretch: normal;
  --typography-390-heading-h1-letter-spacing: 0;
  --typography-390-heading-h1-line-height: 40;
  --typography-390-heading-h1-paragraph-indent: 0;
  --typography-390-heading-h1-paragraph-spacing: 0;
  --typography-390-heading-h1-text-case: none;
  --typography-390-heading-h2-font-size: 28;
  --typography-390-heading-h2-text-decoration: none;
  --typography-390-heading-h2-font-family: AmericanSans;
  --typography-390-heading-h2-font-weight: 400;
  --typography-390-heading-h2-font-style: normal;
  --typography-390-heading-h2-font-stretch: normal;
  --typography-390-heading-h2-letter-spacing: 0;
  --typography-390-heading-h2-line-height: 36;
  --typography-390-heading-h2-paragraph-indent: 0;
  --typography-390-heading-h2-paragraph-spacing: 0;
  --typography-390-heading-h2-text-case: none;
  --typography-390-heading-h3-font-size: 24;
  --typography-390-heading-h3-text-decoration: none;
  --typography-390-heading-h3-font-family: AmericanSans;
  --typography-390-heading-h3-font-weight: 400;
  --typography-390-heading-h3-font-style: normal;
  --typography-390-heading-h3-font-stretch: normal;
  --typography-390-heading-h3-letter-spacing: 0;
  --typography-390-heading-h3-line-height: 32;
  --typography-390-heading-h3-paragraph-indent: 0;
  --typography-390-heading-h3-paragraph-spacing: 0;
  --typography-390-heading-h3-text-case: none;
  --typography-390-heading-h4-font-size: 20;
  --typography-390-heading-h4-text-decoration: none;
  --typography-390-heading-h4-font-family: AmericanSans;
  --typography-390-heading-h4-font-weight: 400;
  --typography-390-heading-h4-font-style: normal;
  --typography-390-heading-h4-font-stretch: normal;
  --typography-390-heading-h4-letter-spacing: 0;
  --typography-390-heading-h4-line-height: 24;
  --typography-390-heading-h4-paragraph-indent: 0;
  --typography-390-heading-h4-paragraph-spacing: 0;
  --typography-390-heading-h4-text-case: none;
  --typography-390-heading-h5-font-size: 18;
  --typography-390-heading-h5-text-decoration: none;
  --typography-390-heading-h5-font-family: AmericanSans;
  --typography-390-heading-h5-font-weight: 400;
  --typography-390-heading-h5-font-style: normal;
  --typography-390-heading-h5-font-stretch: normal;
  --typography-390-heading-h5-letter-spacing: 0;
  --typography-390-heading-h5-line-height: 24;
  --typography-390-heading-h5-paragraph-indent: 0;
  --typography-390-heading-h5-paragraph-spacing: 0;
  --typography-390-heading-h5-text-case: none;
  --typography-390-body-paragraph-font-size: 16;
  --typography-390-body-paragraph-text-decoration: none;
  --typography-390-body-paragraph-font-family: AmericanSans;
  --typography-390-body-paragraph-font-weight: 400;
  --typography-390-body-paragraph-font-style: normal;
  --typography-390-body-paragraph-font-stretch: normal;
  --typography-390-body-paragraph-letter-spacing: 0.16;
  --typography-390-body-paragraph-line-height: 22;
  --typography-390-body-paragraph-paragraph-indent: 0;
  --typography-390-body-paragraph-paragraph-spacing: 0;
  --typography-390-body-paragraph-text-case: none;
  --typography-390-body-paragraph-bold-font-size: 16;
  --typography-390-body-paragraph-bold-text-decoration: none;
  --typography-390-body-paragraph-bold-font-family: AmericanSans;
  --typography-390-body-paragraph-bold-font-weight: 700;
  --typography-390-body-paragraph-bold-font-style: normal;
  --typography-390-body-paragraph-bold-font-stretch: normal;
  --typography-390-body-paragraph-bold-letter-spacing: 0.16;
  --typography-390-body-paragraph-bold-line-height: 22;
  --typography-390-body-paragraph-bold-paragraph-indent: 0;
  --typography-390-body-paragraph-bold-paragraph-spacing: 0;
  --typography-390-body-paragraph-bold-text-case: none;
  --typography-390-body-paragraph-underline-font-size: 16;
  --typography-390-body-paragraph-underline-text-decoration: underline;
  --typography-390-body-paragraph-underline-font-family: AmericanSans;
  --typography-390-body-paragraph-underline-font-weight: 400;
  --typography-390-body-paragraph-underline-font-style: normal;
  --typography-390-body-paragraph-underline-font-stretch: normal;
  --typography-390-body-paragraph-underline-letter-spacing: 0.16;
  --typography-390-body-paragraph-underline-line-height: 22;
  --typography-390-body-paragraph-underline-paragraph-indent: 0;
  --typography-390-body-paragraph-underline-paragraph-spacing: 0;
  --typography-390-body-paragraph-underline-text-case: none;
  --typography-390-body-lead-font-size: 18;
  --typography-390-body-lead-text-decoration: none;
  --typography-390-body-lead-font-family: AmericanSans;
  --typography-390-body-lead-font-weight: 400;
  --typography-390-body-lead-font-style: normal;
  --typography-390-body-lead-font-stretch: normal;
  --typography-390-body-lead-letter-spacing: 0.16;
  --typography-390-body-lead-line-height: 24;
  --typography-390-body-lead-paragraph-indent: 0;
  --typography-390-body-lead-paragraph-spacing: 0;
  --typography-390-body-lead-text-case: none;
  --typography-390-body-lead-bold-font-size: 18;
  --typography-390-body-lead-bold-text-decoration: none;
  --typography-390-body-lead-bold-font-family: AmericanSans;
  --typography-390-body-lead-bold-font-weight: 700;
  --typography-390-body-lead-bold-font-style: normal;
  --typography-390-body-lead-bold-font-stretch: normal;
  --typography-390-body-lead-bold-letter-spacing: 0.16;
  --typography-390-body-lead-bold-line-height: 24;
  --typography-390-body-lead-bold-paragraph-indent: 0;
  --typography-390-body-lead-bold-paragraph-spacing: 0;
  --typography-390-body-lead-bold-text-case: none;
  --typography-390-body-lead-underline-font-size: 18;
  --typography-390-body-lead-underline-text-decoration: underline;
  --typography-390-body-lead-underline-font-family: AmericanSans;
  --typography-390-body-lead-underline-font-weight: 400;
  --typography-390-body-lead-underline-font-style: normal;
  --typography-390-body-lead-underline-font-stretch: normal;
  --typography-390-body-lead-underline-letter-spacing: 0.16;
  --typography-390-body-lead-underline-line-height: 24;
  --typography-390-body-lead-underline-paragraph-indent: 0;
  --typography-390-body-lead-underline-paragraph-spacing: 0;
  --typography-390-body-lead-underline-text-case: none;
  --typography-390-label-label-font-size: 14;
  --typography-390-label-label-text-decoration: none;
  --typography-390-label-label-font-family: AmericanSans;
  --typography-390-label-label-font-weight: 400;
  --typography-390-label-label-font-style: normal;
  --typography-390-label-label-font-stretch: normal;
  --typography-390-label-label-letter-spacing: 0.16;
  --typography-390-label-label-line-height: 16;
  --typography-390-label-label-paragraph-indent: 0;
  --typography-390-label-label-paragraph-spacing: 0;
  --typography-390-label-label-text-case: none;
  --typography-390-label-label-caps-font-size: 14;
  --typography-390-label-label-caps-text-decoration: none;
  --typography-390-label-label-caps-font-family: AmericanSans;
  --typography-390-label-label-caps-font-weight: 400;
  --typography-390-label-label-caps-font-style: normal;
  --typography-390-label-label-caps-font-stretch: normal;
  --typography-390-label-label-caps-letter-spacing: 0.16;
  --typography-390-label-label-caps-line-height: 16;
  --typography-390-label-label-caps-paragraph-indent: 0;
  --typography-390-label-label-caps-paragraph-spacing: 0;
  --typography-390-label-label-caps-text-case: uppercase;
  --typography-390-label-label-medium-font-size: 14;
  --typography-390-label-label-medium-text-decoration: none;
  --typography-390-label-label-medium-font-family: AmericanSans Medium;
  --typography-390-label-label-medium-font-weight: 400;
  --typography-390-label-label-medium-font-style: normal;
  --typography-390-label-label-medium-font-stretch: normal;
  --typography-390-label-label-medium-letter-spacing: 0.16;
  --typography-390-label-label-medium-line-height: 16;
  --typography-390-label-label-medium-paragraph-indent: 0;
  --typography-390-label-label-medium-paragraph-spacing: 0;
  --typography-390-label-label-medium-text-case: none;
  --typography-390-label-label-medium-caps-font-size: 14;
  --typography-390-label-label-medium-caps-text-decoration: none;
  --typography-390-label-label-medium-caps-font-family: AmericanSans Medium;
  --typography-390-label-label-medium-caps-font-weight: 400;
  --typography-390-label-label-medium-caps-font-style: normal;
  --typography-390-label-label-medium-caps-font-stretch: normal;
  --typography-390-label-label-medium-caps-letter-spacing: 0.16;
  --typography-390-label-label-medium-caps-line-height: 16;
  --typography-390-label-label-medium-caps-paragraph-indent: 0;
  --typography-390-label-label-medium-caps-paragraph-spacing: 0;
  --typography-390-label-label-medium-caps-text-case: uppercase;
  --typography-390-helper-caption-font-size: 12;
  --typography-390-helper-caption-text-decoration: none;
  --typography-390-helper-caption-font-family: AmericanSans;
  --typography-390-helper-caption-font-weight: 400;
  --typography-390-helper-caption-font-style: normal;
  --typography-390-helper-caption-font-stretch: normal;
  --typography-390-helper-caption-letter-spacing: 0;
  --typography-390-helper-caption-line-height: 16;
  --typography-390-helper-caption-paragraph-indent: 0;
  --typography-390-helper-caption-paragraph-spacing: 0;
  --typography-390-helper-caption-text-case: none;
  --typography-390-helper-caption-medium-font-size: 12;
  --typography-390-helper-caption-medium-text-decoration: none;
  --typography-390-helper-caption-medium-font-family: AmericanSans Medium;
  --typography-390-helper-caption-medium-font-weight: 400;
  --typography-390-helper-caption-medium-font-style: normal;
  --typography-390-helper-caption-medium-font-stretch: normal;
  --typography-390-helper-caption-medium-letter-spacing: 0;
  --typography-390-helper-caption-medium-line-height: 16;
  --typography-390-helper-caption-medium-paragraph-indent: 0;
  --typography-390-helper-caption-medium-paragraph-spacing: 0;
  --typography-390-helper-caption-medium-text-case: none;
  --typography-1440-heading-title-font-size: 54;
  --typography-1440-heading-title-text-decoration: none;
  --typography-1440-heading-title-font-family: AmericanSans;
  --typography-1440-heading-title-font-weight: 400;
  --typography-1440-heading-title-font-style: normal;
  --typography-1440-heading-title-font-stretch: normal;
  --typography-1440-heading-title-letter-spacing: 0;
  --typography-1440-heading-title-line-height: 64;
  --typography-1440-heading-title-paragraph-indent: 0;
  --typography-1440-heading-title-paragraph-spacing: 0;
  --typography-1440-heading-title-text-case: none;
  --typography-1440-heading-h1-font-size: 42;
  --typography-1440-heading-h1-text-decoration: none;
  --typography-1440-heading-h1-font-family: AmericanSans;
  --typography-1440-heading-h1-font-weight: 400;
  --typography-1440-heading-h1-font-style: normal;
  --typography-1440-heading-h1-font-stretch: normal;
  --typography-1440-heading-h1-letter-spacing: 0;
  --typography-1440-heading-h1-line-height: 50;
  --typography-1440-heading-h1-paragraph-indent: 0;
  --typography-1440-heading-h1-paragraph-spacing: 0;
  --typography-1440-heading-h1-text-case: none;
  --typography-1440-heading-h2-font-size: 32;
  --typography-1440-heading-h2-text-decoration: none;
  --typography-1440-heading-h2-font-family: AmericanSans;
  --typography-1440-heading-h2-font-weight: 400;
  --typography-1440-heading-h2-font-style: normal;
  --typography-1440-heading-h2-font-stretch: normal;
  --typography-1440-heading-h2-letter-spacing: 0;
  --typography-1440-heading-h2-line-height: 40;
  --typography-1440-heading-h2-paragraph-indent: 0;
  --typography-1440-heading-h2-paragraph-spacing: 0;
  --typography-1440-heading-h2-text-case: none;
  --typography-1440-heading-h3-font-size: 28;
  --typography-1440-heading-h3-text-decoration: none;
  --typography-1440-heading-h3-font-family: AmericanSans;
  --typography-1440-heading-h3-font-weight: 400;
  --typography-1440-heading-h3-font-style: normal;
  --typography-1440-heading-h3-font-stretch: normal;
  --typography-1440-heading-h3-letter-spacing: 0;
  --typography-1440-heading-h3-line-height: 36;
  --typography-1440-heading-h3-paragraph-indent: 0;
  --typography-1440-heading-h3-paragraph-spacing: 0;
  --typography-1440-heading-h3-text-case: none;
  --typography-1440-heading-h4-font-size: 24;
  --typography-1440-heading-h4-text-decoration: none;
  --typography-1440-heading-h4-font-family: AmericanSans;
  --typography-1440-heading-h4-font-weight: 400;
  --typography-1440-heading-h4-font-style: normal;
  --typography-1440-heading-h4-font-stretch: normal;
  --typography-1440-heading-h4-letter-spacing: 0;
  --typography-1440-heading-h4-line-height: 32;
  --typography-1440-heading-h4-paragraph-indent: 0;
  --typography-1440-heading-h4-paragraph-spacing: 0;
  --typography-1440-heading-h4-text-case: none;
  --typography-1440-heading-h5-font-size: 20;
  --typography-1440-heading-h5-text-decoration: none;
  --typography-1440-heading-h5-font-family: AmericanSans;
  --typography-1440-heading-h5-font-weight: 400;
  --typography-1440-heading-h5-font-style: normal;
  --typography-1440-heading-h5-font-stretch: normal;
  --typography-1440-heading-h5-letter-spacing: 0;
  --typography-1440-heading-h5-line-height: 24;
  --typography-1440-heading-h5-paragraph-indent: 0;
  --typography-1440-heading-h5-paragraph-spacing: 0;
  --typography-1440-heading-h5-text-case: none;
  --typography-1440-body-paragraph-font-size: 16;
  --typography-1440-body-paragraph-text-decoration: none;
  --typography-1440-body-paragraph-font-family: AmericanSans;
  --typography-1440-body-paragraph-font-weight: 400;
  --typography-1440-body-paragraph-font-style: normal;
  --typography-1440-body-paragraph-font-stretch: normal;
  --typography-1440-body-paragraph-letter-spacing: 0.16;
  --typography-1440-body-paragraph-line-height: 24;
  --typography-1440-body-paragraph-paragraph-indent: 0;
  --typography-1440-body-paragraph-paragraph-spacing: 0;
  --typography-1440-body-paragraph-text-case: none;
  --typography-1440-body-paragraph-bold-font-size: 16;
  --typography-1440-body-paragraph-bold-text-decoration: none;
  --typography-1440-body-paragraph-bold-font-family: AmericanSans;
  --typography-1440-body-paragraph-bold-font-weight: 700;
  --typography-1440-body-paragraph-bold-font-style: normal;
  --typography-1440-body-paragraph-bold-font-stretch: normal;
  --typography-1440-body-paragraph-bold-letter-spacing: 0.16;
  --typography-1440-body-paragraph-bold-line-height: 24;
  --typography-1440-body-paragraph-bold-paragraph-indent: 0;
  --typography-1440-body-paragraph-bold-paragraph-spacing: 0;
  --typography-1440-body-paragraph-bold-text-case: none;
  --typography-1440-body-paragraph-underline-font-size: 16;
  --typography-1440-body-paragraph-underline-text-decoration: underline;
  --typography-1440-body-paragraph-underline-font-family: AmericanSans;
  --typography-1440-body-paragraph-underline-font-weight: 400;
  --typography-1440-body-paragraph-underline-font-style: normal;
  --typography-1440-body-paragraph-underline-font-stretch: normal;
  --typography-1440-body-paragraph-underline-letter-spacing: 0.16;
  --typography-1440-body-paragraph-underline-line-height: 24;
  --typography-1440-body-paragraph-underline-paragraph-indent: 0;
  --typography-1440-body-paragraph-underline-paragraph-spacing: 0;
  --typography-1440-body-paragraph-underline-text-case: none;
  --typography-1440-body-lead-font-size: 18;
  --typography-1440-body-lead-text-decoration: none;
  --typography-1440-body-lead-font-family: AmericanSans;
  --typography-1440-body-lead-font-weight: 400;
  --typography-1440-body-lead-font-style: normal;
  --typography-1440-body-lead-font-stretch: normal;
  --typography-1440-body-lead-letter-spacing: 0.16;
  --typography-1440-body-lead-line-height: 26;
  --typography-1440-body-lead-paragraph-indent: 0;
  --typography-1440-body-lead-paragraph-spacing: 0;
  --typography-1440-body-lead-text-case: none;
  --typography-1440-body-lead-bold-font-size: 18;
  --typography-1440-body-lead-bold-text-decoration: none;
  --typography-1440-body-lead-bold-font-family: AmericanSans;
  --typography-1440-body-lead-bold-font-weight: 700;
  --typography-1440-body-lead-bold-font-style: normal;
  --typography-1440-body-lead-bold-font-stretch: normal;
  --typography-1440-body-lead-bold-letter-spacing: 0.16;
  --typography-1440-body-lead-bold-line-height: 26;
  --typography-1440-body-lead-bold-paragraph-indent: 0;
  --typography-1440-body-lead-bold-paragraph-spacing: 0;
  --typography-1440-body-lead-bold-text-case: none;
  --typography-1440-body-lead-underline-font-size: 18;
  --typography-1440-body-lead-underline-text-decoration: underline;
  --typography-1440-body-lead-underline-font-family: AmericanSans;
  --typography-1440-body-lead-underline-font-weight: 400;
  --typography-1440-body-lead-underline-font-style: normal;
  --typography-1440-body-lead-underline-font-stretch: normal;
  --typography-1440-body-lead-underline-letter-spacing: 0.16;
  --typography-1440-body-lead-underline-line-height: 26;
  --typography-1440-body-lead-underline-paragraph-indent: 0;
  --typography-1440-body-lead-underline-paragraph-spacing: 0;
  --typography-1440-body-lead-underline-text-case: none;
  --typography-1440-label-label-font-size: 14;
  --typography-1440-label-label-text-decoration: none;
  --typography-1440-label-label-font-family: AmericanSans;
  --typography-1440-label-label-font-weight: 400;
  --typography-1440-label-label-font-style: normal;
  --typography-1440-label-label-font-stretch: normal;
  --typography-1440-label-label-letter-spacing: 0.16;
  --typography-1440-label-label-line-height: 16;
  --typography-1440-label-label-paragraph-indent: 0;
  --typography-1440-label-label-paragraph-spacing: 0;
  --typography-1440-label-label-text-case: none;
  --typography-1440-label-label-caps-font-size: 14;
  --typography-1440-label-label-caps-text-decoration: none;
  --typography-1440-label-label-caps-font-family: AmericanSans;
  --typography-1440-label-label-caps-font-weight: 400;
  --typography-1440-label-label-caps-font-style: normal;
  --typography-1440-label-label-caps-font-stretch: normal;
  --typography-1440-label-label-caps-letter-spacing: 0.16;
  --typography-1440-label-label-caps-line-height: 16;
  --typography-1440-label-label-caps-paragraph-indent: 0;
  --typography-1440-label-label-caps-paragraph-spacing: 0;
  --typography-1440-label-label-caps-text-case: uppercase;
  --typography-1440-label-label-medium-font-size: 14;
  --typography-1440-label-label-medium-text-decoration: none;
  --typography-1440-label-label-medium-font-family: AmericanSans Medium;
  --typography-1440-label-label-medium-font-weight: 400;
  --typography-1440-label-label-medium-font-style: normal;
  --typography-1440-label-label-medium-font-stretch: normal;
  --typography-1440-label-label-medium-letter-spacing: 0.16;
  --typography-1440-label-label-medium-line-height: 16;
  --typography-1440-label-label-medium-paragraph-indent: 0;
  --typography-1440-label-label-medium-paragraph-spacing: 0;
  --typography-1440-label-label-medium-text-case: none;
  --typography-1440-label-label-medium-caps-font-size: 14;
  --typography-1440-label-label-medium-caps-text-decoration: none;
  --typography-1440-label-label-medium-caps-font-family: AmericanSans Medium;
  --typography-1440-label-label-medium-caps-font-weight: 400;
  --typography-1440-label-label-medium-caps-font-style: normal;
  --typography-1440-label-label-medium-caps-font-stretch: normal;
  --typography-1440-label-label-medium-caps-letter-spacing: 0.16;
  --typography-1440-label-label-medium-caps-line-height: 16;
  --typography-1440-label-label-medium-caps-paragraph-indent: 0;
  --typography-1440-label-label-medium-caps-paragraph-spacing: 0;
  --typography-1440-label-label-medium-caps-text-case: uppercase;
  --typography-1440-helper-caption-font-size: 12;
  --typography-1440-helper-caption-text-decoration: none;
  --typography-1440-helper-caption-font-family: AmericanSans;
  --typography-1440-helper-caption-font-weight: 400;
  --typography-1440-helper-caption-font-style: normal;
  --typography-1440-helper-caption-font-stretch: normal;
  --typography-1440-helper-caption-letter-spacing: 0;
  --typography-1440-helper-caption-line-height: 16;
  --typography-1440-helper-caption-paragraph-indent: 0;
  --typography-1440-helper-caption-paragraph-spacing: 0;
  --typography-1440-helper-caption-text-case: none;
  --typography-1440-helper-caption-medium-font-size: 12;
  --typography-1440-helper-caption-medium-text-decoration: none;
  --typography-1440-helper-caption-medium-font-family: AmericanSans Medium;
  --typography-1440-helper-caption-medium-font-weight: 400;
  --typography-1440-helper-caption-medium-font-style: normal;
  --typography-1440-helper-caption-medium-font-stretch: normal;
  --typography-1440-helper-caption-medium-letter-spacing: 0;
  --typography-1440-helper-caption-medium-line-height: 16;
  --typography-1440-helper-caption-medium-paragraph-indent: 0;
  --typography-1440-helper-caption-medium-paragraph-spacing: 0;
  --typography-1440-helper-caption-medium-text-case: none;
}`;

const componentStyles = css`
  ${lightThemeStyles}

  :root,
  :host {
    --spacing-20: 1.25rem;
    --animation-speed: 50ms;
    --animation-easing: ease-in;
  }

  :root [disabled],
  :host [disabled] {
    --color-light-mode-interactive-disabled: #b4c1cf;
  }

  :host {
    box-sizing: border-box;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
  }

  :host *,
  :host *::before,
  :host *::after {
    box-sizing: inherit;
  }

  :host(.focus-visible),
  :host(:focus-within) {
    border-radius: var(--radius-sm);
    outline-offset: 2px;
    outline: 2px solid var(--color-light-mode-interactive-focus-outline-focus);
  }

  :host *:focus-visible {
    outline: none;
  }

  [hidden] {
    display: none !important;
  }

  [disabled] {
    pointer-events: none;
  }

  :host .sr-only {
    clip: rect(1px, 1px, 1px, 1px);
    clip-path: inset(50%);
    height: 1px;
    width: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
  }
`;

var styles = css`
  ${componentStyles}

  :host(.focus-visible),
  :host(:focus-within) {
    outline: 0;
  }

  :host(adc-radio-group) {
    font-family: var(--typography-1440-label-label-font-family);
    font-size: calc(var(--typography-1440-label-label-font-size) * 1px);
    line-height: calc(var(--typography-1440-label-label-line-height) * 1px);
    font-weight: var(--typography-1440-label-label-font-weight);
    letter-spacing: calc(var(--typography-1440-label-label-letter-spacing) * 1px);

    color: var(--color-light-mode-text-text-secondary);
    display: block;
    margin-bottom: var(--spacing-4);
  }

  :host(adc-radio-button) {
    display: inline-flex;
    outline: none;
  }

  :host(:focus-visible) .adc-radio-button__control,
  :host(:focus-within) .adc-radio-button__control {
    border: 1px solid var(--color-light-mode-border-border-interactive);
  }

  :host(:focus-visible) .adc-radio-button__control::after,
  :host(:focus-within) .adc-radio-button__control::after {
    border-radius: 50%;

    background: rgba(0 120 210 / 0.24);
    height: var(--spacing-32);
    transition: background 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      width 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, height 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    width: var(--spacing-32);
  }

  :host(adc-radio-button:hover) .adc-radio-button__control {
    border: 1px solid var(--color-light-mode-text-text-secondary);
  }

  :host(adc-radio-button:hover) .adc-radio-button__control::after {
    border-radius: 50%;

    background: var(--color-light-mode-interactive-hover-hover-on-container-default);
    height: var(--spacing-32);
    transition: background 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      width 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, height 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    width: var(--spacing-32);
  }

  :host([label-position="left"]) .adc-radio-button__label {
    flex-direction: row-reverse;
  }

  :host([label-position="left"]) .adc-radio-button__control {
    margin-left: var(--spacing-8);
    margin-right: 0;
  }

  :host([orientation="horizontal"]) {
    margin-right: var(--spacing-16);
  }

  :host([orientation="horizontal"]:last-child) {
    margin-right: 0;
  }

  :host([orientation="horizontal"][label-position="left"]) {
    margin-left: var(--spacing-16);
    margin-right: 0;
  }

  :host([orientation="horizontal"][label-position="left"]:first-child) {
    margin-left: 0;
  }

  :host(adc-radio-group[orientation="vertical"]) .adc-radio-group__label-text {
    margin-bottom: var(--spacing-8);
  }

  :host(adc-radio-button[orientation="vertical"]) {
    display: flex;
    margin-bottom: var(--spacing-16);
  }

  :host(adc-radio-button[orientation="vertical"]) .adc-radio-button__label {
    height: auto;
  }

  :host([orientation="vertical"]:last-child) {
    margin-bottom: 0;
  }

  :host([disabled]) {
    pointer-events: none;
  }

  :host([disabled]) .adc-radio-button__control {
    border-color: var(--color-light-mode-interactive-disabled-disabled-on-border-interactive);
  }

  :host([disabled]) .adc-radio-button__control::before {
    border-color: var(--color-light-mode-interactive-disabled-disabled-on-border-interactive);
  }

  :host([disabled]) .adc-radio-button__label {
    color: var(--color-light-mode-text-text-disabled);
  }

  :host([invalid]) .adc-radio-button__control {
    border: 1px solid var(--color-light-mode-border-border-status-error);
  }

  :host([invalid]) .adc-radio-group--form-requirement {
    font-family: var(
      --adc-typography-helper-font-family,
      var(--adc-typography-font-family, "American Sans", sans-serif)
    );
    font-size: var(--adc-typography-helper-font-size, 0.75rem);
    line-height: var(--adc-typography-helper-line-height, 1rem);
    font-weight: var(--adc-typography-helper-font-weight, 400);

    color: var(--color-light-mode-text-text-status-error);
    display: block;
    overflow: visible;
  }

  :host([checked]) .adc-radio-button__control {
    border-color: var(--color-light-mode-border-border-interactive);
  }

  :host([checked]) .adc-radio-button__control:hover {
    border-color: var(--color-light-mode-border-border-interactive);
  }

  :host([checked]) .adc-radio-button__control::before {
    background: var(--color-light-mode-background-container-interactive);
    border-color: var(--color-light-mode-border-border-interactive);
  }

  :host([checked][disabled]) .adc-radio-button__control {
    border-color: var(--color-light-mode-interactive-disabled-disabled-on-border-interactive);
  }

  :host([checked][disabled]) .adc-radio-button__control::before {
    background: var(--color-light-mode-interactive-disabled-disabled-on-container-interactive);
    border-color: var(--color-light-mode-interactive-disabled-disabled-on-border-interactive);
  }

  .adc-radio-group {
    margin: 0;
    padding: 0;
    border: 0;
  }

  .adc-radio-group__label-text {
    align-items: center;
    display: flex;
    margin-bottom: var(--spacing-8);
  }

  .adc-radio-group__label-text--required::after {
    border-radius: 50%;

    background: var(--color-light-mode-background-container-status-error);
    content: "";
    height: 6px;
    margin-left: var(--spacing-4);
    width: 6px;
  }

  .adc-radio-group__label-text--invalid {
    color: var(--color-light-mode-text-text-status-error);
  }

  .adc-radio-group__icon-error {
    margin-right: var(--spacing-4);
  }

  .adc-radio-button__control {
    border-radius: 50%;

    align-items: center;
    background: var(--color-light-mode-background-container-default);
    border: 1px solid var(--color-light-mode-border-border-default);
    box-sizing: border-box;
    display: flex;
    height: var(--spacing-24);
    justify-content: center;
    margin-right: var(--spacing-8);
    position: relative;
    transition: border 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      background 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      height 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, width 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    width: var(--spacing-24);
    white-space: nowrap;
  }

  .adc-radio-button__control::before,
  .adc-radio-button__control::after {
    position: absolute;
  }

  .adc-radio-button__control::before {
    border-radius: 50%;

    border: 1px solid var(--color-light-mode-border-border-default);
    box-sizing: border-box;
    content: "";
    display: flex;
    height: var(--spacing-16);
    width: var(--spacing-16);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .adc-radio-button__control::after {
    border-radius: 50%;

    box-sizing: border-box;
    content: "";
    display: flex;
    height: var(--spacing-12);
    transition: background 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      height 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, width 180ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    width: var(--spacing-12);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;
  }

  .adc-radio-button__control:disabled {
    pointer-events: none;
  }

  .adc-radio-button__label {
    font-family: var(--typography-1440-body-paragraph-font-family);
    font-size: calc(var(--typography-1440-body-paragraph-font-size) * 1px);
    line-height: calc(var(--typography-1440-body-paragraph-line-height) * 1px);
    font-weight: var(--typography-1440-body-paragraph-font-weight);
    letter-spacing: var(--typography-1440-body-paragraph-letter-spacing);

    align-items: center;
    color: var(--color-light-mode-text-text-default);
    cursor: pointer;
    display: flex;
    height: var(--spacing-48);
    justify-content: start;
    margin: 0;
    position: relative;
    vertical-align: middle;
    white-space: nowrap;
    width: 100%;
  }

  .adc-radio-button__label:hover .adc-radio-button__control {
    border: 1px solid var(--color-light-mode-border-border-default);
  }

  .adc-radio-button__label:hover .adc-radio-button__control::after {
    background: var(--color-light-mode-interactive-hover-hover-on-container-default);
  }

  .adc-radio-button__label--hidden .adc-radio-button__control {
    margin: var(--spacing-4) 0 !important;
  }

  .adc-radio-group--form-requirement {
    display: none;
    overflow: hidden;
    margin: var(--spacing-8) 0 0;
  }

  .visually-hidden {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

var __defProp$2 = Object.defineProperty;
var __getOwnPropDesc$1 = Object.getOwnPropertyDescriptor;
var __decorateClass$1 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$1(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--) if (decorator = decorators[i]) result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp$2(target, key, result);
  return result;
};
const RADIO_CHILDREN = ["adc-radio-button"];
class RadioGroup extends LitElement {
  constructor() {
    super(...arguments);
    this.required = false;
    this.disabled = false;
    this.invalid = false;
    this.labelPosition = "right";
    this.orientation = "horizontal";
    this.validityMessage = "";
  }
  getAllRadios() {
    return [...this.querySelectorAll(RADIO_CHILDREN.join(","))].filter(el => {
      return RADIO_CHILDREN.includes(el.tagName.toLowerCase());
    });
  }
  handleRadioClick(event) {
    const target = event.target;
    const checkedRadio = target.closest(RADIO_CHILDREN.map(selector => `${selector}:not([disabled])`).join(","));
    if (checkedRadio) {
      this.value = checkedRadio.value;
      this.getAllRadios().forEach(radio => {
        radio.checked = radio === checkedRadio;
        radio.input.tabIndex = radio === checkedRadio ? 0 : -1;
      });
    }
  }
  handleKeyDown(event) {
    var _a;
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
      const radios = this.getAllRadios().filter(radio => !radio.disabled);
      const checkedRadio = (_a = radios.find(radio => radio.checked)) != null ? _a : radios[0];
      const incr = ["ArrowUp", "ArrowLeft"].includes(event.key) ? -1 : 1;
      let index = radios.indexOf(checkedRadio) + incr;
      if (index < 0) {
        index = radios.length - 1;
      }
      if (index > radios.length - 1) {
        index = 0;
      }
      this.getAllRadios().forEach(radio => {
        radio.checked = false;
        radio.input.tabIndex = -1;
      });
      radios[index].focus();
      radios[index].checked = true;
      radios[index].input.tabIndex = 0;
      this.value = radios[index].value;
      event.preventDefault();
    }
  }
  handleSlotChange() {
    const radios = this.getAllRadios();
    const checkedRadio = radios.find(radio => radio.checked);
    radios.forEach(radio => {
      radio.checked = radio === checkedRadio || radio.value === this.value;
      radio.required = this.required;
      radio.name = this.name;
      radio.input.tabIndex = -1;
    });
    if (checkedRadio) {
      checkedRadio.input.tabIndex = 0;
    } else if (radios.length > 0) {
      radios[0].input.tabIndex = 0;
    }
  }
  handleValueChange() {
    const radios = this.getAllRadios();
    if (this.hasUpdated) {
      radios.forEach(radio => {
        radio.checked = radio.value === this.value;
      });
      emit(this, "adc-radio-group-changed", {
        detail: {
          value: this.value
        }
      });
    }
  }
  handleDisabledChange() {
    const radios = this.getAllRadios();
    if (this.hasUpdated) {
      radios.forEach(radio => {
        radio.disabled = this.disabled;
      });
    }
  }
  handleOrientationChange() {
    const radios = this.getAllRadios();
    if (this.hasUpdated) {
      radios.forEach(radio => {
        radio.orientation = this.orientation;
      });
    }
  }
  handleLabeLPositionChange() {
    const radios = this.getAllRadios();
    if (this.hasUpdated) {
      radios.forEach(radio => {
        radio.labelPosition = this.labelPosition;
      });
    }
  }
  handleRequiredChange() {
    const radios = this.getAllRadios();
    if (this.hasUpdated) {
      radios.forEach(radio => {
        radio.required = this.required;
      });
    }
  }
  async updated(changedProperties) {
    await this.updateComplete;
    this.getAllRadios().forEach(radio => {
      radio.invalid = false;
    });
    if (changedProperties.has("invalid")) {
      this.getAllRadios().forEach(radio => {
        radio.invalid = this.invalid;
      });
    }
  }
  render() {
    const classes = {
      "adc-radio-group__label-text--required": this.required,
      "adc-radio-group__label-text--invalid": this.invalid
    };
    return html`
      <fieldset part="base" class="adc-radio-group">
        <legend part="label" class="adc-radio-group__label-text ${classMap(classes)}">
          ${this.invalid ? html`<adc-icon icon="signal:error" size="16" outlined class="adc-radio-group__icon-error"></adc-icon>` : void 0}
          <slot name="label-text">${this.labelText}</slot>
        </legend>
        <slot
          @click=${this.handleRadioClick}
          @keydown=${this.handleKeyDown}
          @slotchange=${this.handleSlotChange}
        ></slot>
        <div class="adc-radio-group--form-requirement" aria-live="polite">
          <slot name="validity-message"> ${this.validityMessage} </slot>
        </div>
      </fieldset>
    `;
  }
}
RadioGroup.styles = styles;
__decorateClass$1([property({
  type: Boolean,
  reflect: true
})], RadioGroup.prototype, "required", 2);
__decorateClass$1([property({
  type: Boolean,
  reflect: true
})], RadioGroup.prototype, "disabled", 2);
__decorateClass$1([property({
  type: Boolean,
  reflect: true
})], RadioGroup.prototype, "invalid", 2);
__decorateClass$1([property({
  attribute: "label-position"
})], RadioGroup.prototype, "labelPosition", 2);
__decorateClass$1([property({
  attribute: "label-text"
})], RadioGroup.prototype, "labelText", 2);
__decorateClass$1([property()], RadioGroup.prototype, "name", 2);
__decorateClass$1([property({
  reflect: true
})], RadioGroup.prototype, "value", 2);
__decorateClass$1([property({
  reflect: true
})], RadioGroup.prototype, "orientation", 2);
__decorateClass$1([property({
  attribute: "validity-message"
})], RadioGroup.prototype, "validityMessage", 2);
__decorateClass$1([watch("value", {
  waitUntilFirstUpdate: true
})], RadioGroup.prototype, "handleValueChange", 1);
__decorateClass$1([watch("disabled", {
  waitUntilFirstUpdate: true
})], RadioGroup.prototype, "handleDisabledChange", 1);
__decorateClass$1([watch("orientation", {
  waitUntilFirstUpdate: true
})], RadioGroup.prototype, "handleOrientationChange", 1);
__decorateClass$1([watch("labelPosition", {
  waitUntilFirstUpdate: true
})], RadioGroup.prototype, "handleLabeLPositionChange", 1);
__decorateClass$1([watch("required", {
  waitUntilFirstUpdate: true
})], RadioGroup.prototype, "handleRequiredChange", 1);
try {
  customElements.define("adc-radio-group", RadioGroup);
} catch (e) {}

class FormDataEventPolyfill extends Event {
  constructor(formData) {
    super("formdata");
    this.formData = formData;
  }
}
class FormDataPolyfill extends FormData {
  constructor(form) {
    super(form);
    this.form = form;
    form.dispatchEvent(new FormDataEventPolyfill(this));
  }
  append(name, value) {
    let input = this.form.elements[name];
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      this.form.appendChild(input);
    }
    if (this.has(name)) {
      const entries = this.getAll(name);
      const index = entries.indexOf(input.value);
      if (index !== -1) {
        entries.splice(index, 1);
      }
      entries.push(value);
      this.set(name, entries);
    } else {
      super.append(name, value);
    }
    input.value = value;
  }
}
function supportsFormDataEvent() {
  const form = document.createElement("form");
  let isSupported = false;
  document.body.append(form);
  form.addEventListener("submit", event => {
    new FormData(event.target);
    event.preventDefault();
  });
  form.addEventListener("formdata", () => isSupported = true);
  form.dispatchEvent(new Event("submit", {
    cancelable: true
  }));
  form.remove();
  return isSupported;
}
function polyfillFormData() {
  if (!window.FormData || supportsFormDataEvent()) {
    return;
  }
  window.FormData = FormDataPolyfill;
  window.addEventListener("submit", event => {
    if (!event.defaultPrevented) {
      new FormData(event.target);
    }
  });
}
if (document.readyState === "complete") {
  polyfillFormData();
} else {
  window.addEventListener("DOMContentLoaded", () => polyfillFormData());
}

var __defProp$1 = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp$1(obj, key, {
  enumerable: true,
  configurable: true,
  writable: true,
  value
}) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {})) if (__hasOwnProp.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols) for (var prop of __getOwnPropSymbols(b)) {
    if (__propIsEnum.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  }
  return a;
};
class FormSubmitController {
  constructor(host, options) {
    (this.host = host).addController(this);
    this.options = __spreadValues({
      form: input => this.closestElement("form", input),
      name: input => input.name,
      value: input => input.value,
      disabled: input => input.disabled,
      reportValidity: input => {
        return typeof input.reportValidity === "function" ? input.reportValidity() : true;
      }
    }, options);
    if (this.options.form(this.host)) {
      this.options.form(this.host).noValidate = true;
    }
    this.handleFormData = this.handleFormData.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }
  hostConnected() {
    this.form = this.options.form(this.host);
    if (this.form) {
      this.form.addEventListener("formdata", this.handleFormData);
      this.form.addEventListener("submit", this.handleFormSubmit);
    }
  }
  hostDisconnected() {
    if (this.form) {
      this.form.removeEventListener("formdata", this.handleFormData);
      this.form.removeEventListener("submit", this.handleFormSubmit);
      this.form = void 0;
    }
  }
  handleFormData(event) {
    const disabled = this.options.disabled(this.host);
    const name = this.options.name(this.host);
    const value = this.options.value(this.host);
    if (!disabled && typeof name === "string" && typeof value !== "undefined") {
      if (Array.isArray(value)) {
        value.forEach(v => {
          event.formData.append(name, v.toString());
        });
      } else {
        event.formData.append(name, value.toString());
      }
    }
  }
  handleFormSubmit(event) {
    const disabled = this.options.disabled(this.host);
    if (this.form && !disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
  submit(submitter) {
    if (this.form) {
      const button = document.createElement("button");
      button.type = "submit";
      button.style.position = "absolute";
      button.style.width = "0";
      button.style.height = "0";
      button.style.clipPath = "inset(50%)";
      button.style.overflow = "hidden";
      button.style.whiteSpace = "nowrap";
      if (submitter) {
        ["formaction", "formmethod", "formnovalidate", "formtarget"].forEach(attr => {
          if (submitter.hasAttribute(attr)) {
            button.setAttribute(attr, submitter.getAttribute(attr));
          }
        });
      }
      this.form.append(button);
      button.click();
      button.remove();
    }
  }
  closestElement(selector, baseElement) {
    function closestFrom(el) {
      if (!el || el === document || el === window) {
        return null;
      }
      if (el.assignedSlot) {
        el = el.assignedSlot;
      }
      const found = el.closest(selector);
      return found ? found : closestFrom(el.getRootNode().host);
    }
    return closestFrom(baseElement);
  }
}

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--) if (decorator = decorators[i]) result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
class RadioButton extends LitElement {
  constructor() {
    super(...arguments);
    this.formSubmitController = new FormSubmitController(this, {
      value: control => control.checked ? control.value : void 0
    });
    this.hasFocus = false;
    this.checked = false;
    this.disabled = false;
    this.hideLabel = false;
    this.labelPosition = "right";
    this.invalid = false;
    this.required = false;
    this.orientation = "horizontal";
  }
  click() {
    this.input.click();
  }
  focus(options) {
    this.input.focus(options);
  }
  blur() {
    this.input.blur();
  }
  reportValidity() {
    return this.input.reportValidity();
  }
  checkValidity() {
    return this.input.checkValidity();
  }
  setCustomValidity(message) {
    this.input.setCustomValidity(message);
    this.invalid = !this.input.checkValidity();
  }
  handleBlur() {
    this.hasFocus = false;
    emit(this, "adc-blur");
  }
  handleClick() {
    if (!this.disabled) {
      this.checked = true;
    }
  }
  handleFocus() {
    this.hasFocus = true;
    emit(this, "adc-focus");
  }
  handleInvalid() {
    this.invalid = true;
  }
  handleCheckedChange() {
    if (this.hasUpdated) {
      emit(this, "adc-change");
    }
  }
  handleDisabledChange() {
    this.setAttribute("aria-disabled", this.disabled ? "true" : "false");
    if (this.hasUpdated) {
      this.input.disabled = this.disabled;
      this.invalid = !this.input.checkValidity();
    }
  }
  render() {
    const labelClasses = {
      "adc-radio-button__label--hidden": this.hideLabel,
      "adc-radio-button__label--checked": this.checked,
      "adc-radio-button__label--disabled": this.disabled,
      "adc-radio-button__label--invalid": this.invalid,
      "adc-radio-button__label--required": this.required,
      "adc-radio-button__label--focused": this.hasFocus
    };
    const innerLabelClasses = {
      "visually-hidden": this.hideLabel
    };
    return html`
      <input
        id="input"
        class="adc-radio-button__input visually-hidden"
        type="radio"
        .checked=${live(this.checked)}
        .disabled=${this.disabled}
        .required=${this.required}
        name=${ifDefined(this.name)}
        value=${ifDefined(this.value)}
        @invalid=${this.handleInvalid}
        @click=${this.handleClick}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
      />
      <label part="base" for="input" class="adc-radio-button__label ${classMap(labelClasses)}">
        <span part="control" class="adc-radio-button__control"></span>
        <span part="label" class="adc-radio-button__label-text ${classMap(innerLabelClasses)}">
          <slot>${this.labelText}</slot>
        </span>
      </label>
    `;
  }
}
RadioButton.styles = styles;
__decorateClass([query("input[type=radio]")], RadioButton.prototype, "input", 2);
__decorateClass([state()], RadioButton.prototype, "hasFocus", 2);
__decorateClass([property({
  type: Boolean,
  reflect: true
})], RadioButton.prototype, "checked", 2);
__decorateClass([property({
  type: Boolean,
  reflect: true
})], RadioButton.prototype, "disabled", 2);
__decorateClass([property({
  type: Boolean,
  reflect: true,
  attribute: "hide-label"
})], RadioButton.prototype, "hideLabel", 2);
__decorateClass([property({
  reflect: true,
  attribute: "label-position"
})], RadioButton.prototype, "labelPosition", 2);
__decorateClass([property({
  attribute: "label-text"
})], RadioButton.prototype, "labelText", 2);
__decorateClass([property()], RadioButton.prototype, "value", 2);
__decorateClass([property({
  type: Boolean,
  reflect: true
})], RadioButton.prototype, "invalid", 2);
__decorateClass([property({
  type: Boolean,
  reflect: true
})], RadioButton.prototype, "required", 2);
__decorateClass([property()], RadioButton.prototype, "name", 2);
__decorateClass([property({
  reflect: true
})], RadioButton.prototype, "orientation", 2);
__decorateClass([watch("checked")], RadioButton.prototype, "handleCheckedChange", 1);
__decorateClass([watch("disabled", {
  waitUntilFirstUpdate: true
})], RadioButton.prototype, "handleDisabledChange", 1);
try {
  customElements.define("adc-radio-button", RadioButton);
} catch (e) {}
